import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export interface StampSignatureImage {
  /** An object URL for the signature image, or null when there is none to show. */
  src: string | null;
  /** True while the image is on its way. The caller shows nothing extra meanwhile. */
  pending: boolean;
}

/**
 * The customer's stamp signature (`GET /tickets/:id/stamp-signature`) as an
 * object URL for an `<img>`.
 *
 * Pass `enabled` only when the ticket's frozen stamp says `has_signature` —
 * a ticket with no stamp or no signature makes no request at all.
 *
 * The server sends `no-store`, and nothing here keeps a copy either: `gcTime: 0`
 * drops the bytes as soon as nothing reads them, and the object URL is revoked
 * when the ticket changes, the query is disabled, or the caller unmounts.
 *
 * A 404 or any other failure leaves `src` null and `pending` false, so the
 * caller falls back to its text. A 404 is not retried: it is an answer.
 */
export function useStampSignatureUrl(ticketId: string | null | undefined, enabled: boolean): StampSignatureImage {
  const active = enabled && !!ticketId;
  const query = useQuery({
    queryKey: ['ticket-stamp-signature', ticketId],
    queryFn: () => api.tickets.getStampSignature(ticketId!).then((r) => r.data),
    enabled: active,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const blob = active && query.data instanceof Blob && query.data.type.startsWith('image/') ? query.data : null;

  // The URL is paired with the Blob it was made from, so a render between a
  // ticket change and this effect never hands out the previous ticket's
  // (already revoked) URL.
  const [made, setMade] = useState<{ blob: Blob; url: string } | null>(null);
  useEffect(() => {
    if (!blob) {
      setMade(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setMade({ blob, url });
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const src = blob && made?.blob === blob ? made.url : null;
  return {
    src,
    pending: active && !query.isError && src === null && (query.isPending || blob !== null),
  };
}
